import {
  Input as AntdInput,
  Button,
  Card,
  InputNumber,
  Modal,
  Progress,
  Select,
  Spin,
  Upload,
  notification,
} from "antd";
import { Autocomplete, Input, Button as MantineButton } from "@mantine/core";
import {
  IconArchive,
  IconArrowLeft,
  IconArrowRight,
  IconBuilding,
  IconCalendar,
  IconDownload,
  IconEye,
  IconFileText,
  IconFolder,
  IconMap,
  IconMapPin,
  IconPackage,
  IconPencil,
  IconSearch,
  IconSettings,
  IconUpload,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiUserGetMe } from "@/api/auth";
import { apiUserGetPartners } from "@/api";
import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useTranslation } from "react-i18next";
import { docKindOf } from "@/lib/docAnnotate/exporter";
import { documentAnnotateHref } from "@/lib/docAnnotate/href";

const ANNOTATABLE_FOLDER = "A";

type NodeType = "building" | "zone" | "batch" | "package";

interface NodeProgressData {
  id: string;
  name: string;
  progress: number;
  completed: number;
  total: number;
}

const NODE_TYPE_LABELS: Record<NodeType, string> = {
  building: "建築",
  zone: "分區",
  batch: "分批",
  package: "構件包",
};

function getProgressColor(
  progress: number,
  lowThreshold = 30,
  highThreshold = 70,
) {
  if (progress < lowThreshold) return "#ff4d4f";
  if (progress < highThreshold) return "#1890ff";
  return "#52c41a";
}

function CircularProgress({
  value,
  label,
  isActive,
  onClick,
  lowThreshold = 30,
  highThreshold = 70,
}: {
  value: number;
  label: string;
  isActive: boolean;
  onClick: () => void;
  lowThreshold?: number;
  highThreshold?: number;
}) {
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (value / 100) * circumference;
  const color = getProgressColor(value, lowThreshold, highThreshold);

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all duration-300 hover:bg-gray-100 ${
        isActive ? "bg-gray-100 shadow-md" : ""
      }`}
      style={{
        border: "none",
        background: isActive ? "#f5f5f5" : "transparent",
        cursor: "pointer",
      }}
    >
      <div className="relative w-24 h-24">
        <svg className="transform -rotate-90 w-24 h-24">
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke="#e8e8e8"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke={color}
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-300"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold" style={{ color }}>
            {value}%
          </span>
        </div>
      </div>
      <span
        className={`text-sm font-semibold ${isActive ? "text-gray-800" : "text-gray-500"}`}
      >
        {label}
      </span>
    </button>
  );
}

function WorkItemProgress({
  name,
  progress,
  completed,
  total,
  lowThreshold = 30,
  highThreshold = 70,
}: {
  name: string;
  progress: number;
  completed: number;
  total: number;
  lowThreshold?: number;
  highThreshold?: number;
}) {
  const color = getProgressColor(progress, lowThreshold, highThreshold);

  return (
    <div className="py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium" style={{ color: "#6F6F6F" }}>
          {name}
        </span>
        <span className="text-sm font-semibold" style={{ color }}>
          {completed}/{total} ({progress}%)
        </span>
      </div>
      <Progress
        percent={progress}
        showInfo={false}
        strokeColor={color}
        trailColor="#e8e8e8"
        size="small"
      />
    </div>
  );
}

function QuotationPage() {
  const [quotationData, setQuotationData] = useState({
    date: "",
    remarks: "",
    partner: "",
    partnerAddress: {
      city: null,
      district: null,
      postalCode: null,
      address: null,
    },
    projectStatus: "quotation",
    name: "",
    company: localStorage.getItem("companyId"),
  });
  const [newFileUpload, setNewFileUpload] = useState([]);
  const [contractFileUpload, setContractFileUpload] = useState([]);
  const [partnerData, setPartnerData] = useState([]);
  const [api, contextHolder] = notification.useNotification();
  const [basicInfoExpanded, setBasicInfoExpanded] = useState(false);
  const [fileUploadState, setFileUploadState] = useState(false);
  const [contractUploadState, setContractUploadState] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [folderSelectModalOpen, setFolderSelectModalOpen] = useState(false);
  const [fileSearch, setFileSearch] = useState("");
  const [fileFolderFilter, setFileFolderFilter] = useState<string>("all");
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [activeProgressSection, setActiveProgressSection] = useState<
    "building" | "zone" | "batch" | "package"
  >("building");
  const [showSettings, setShowSettings] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [lowThreshold, setLowThreshold] = useState<number | null>(30);
  const [highThreshold, setHighThreshold] = useState<number | null>(70);
  const FOLDERS = [
    { id: "A", name: "A. 構件" },
    { id: "B", name: "B. 零件" },
    { id: "C", name: "C. 安裝平立面圖" },
    { id: "D", name: "D. 建築平立方圖（設計端）" },
    { id: "E", name: "E. 結構平立方圖（設計端）" },
    { id: "F", name: "F. 材質證明" },
    { id: "G", name: "G. 無輻射證明" },
    { id: "H", name: "H. 會議記錄" },
    { id: "I", name: "I. 注意事項" },
  ];

  const stripUuidPrefix = (value: string) =>
    value.replace(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i,
      "",
    );

  const escapeRegExp = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const matchesSearch = (fileName: string, term: string) => {
    const trimmed = term.trim();
    if (!trimmed) return true;
    const boundary = /-\d+$/.test(trimmed) ? "(?!\\d)" : "";
    return new RegExp(escapeRegExp(trimmed) + boundary, "i").test(fileName);
  };

  const getFilesInFolder = (folder: string) => {
    if (!Array.isArray(newFileUpload)) return [];
    const filesInFolder = newFileUpload.filter(
      (file: any) => file?.folder === folder,
    );
    const term = fileSearch.trim();
    if (!term) return filesInFolder;
    return filesInFolder.filter((file: any) =>
      matchesSearch(file?.name || "", term),
    );
  };

  const getMatchedFilesAcrossFolders = (): any[] => {
    if (!Array.isArray(newFileUpload)) return [];
    const term = fileSearch.trim();
    return (newFileUpload as any[]).filter((file: any) => {
      if (fileFolderFilter !== "all" && file?.folder !== fileFolderFilter) {
        return false;
      }
      return matchesSearch(file?.name || "", term);
    });
  };

  const isFileFilterActive =
    fileSearch.trim().length > 0 || fileFolderFilter !== "all";

  const getNodesOfType = (nodeType: NodeType): any[] => {
    const projectNode = (quotationData as any)?.projectNode;
    if (!projectNode || !Array.isArray(projectNode)) return [];
    return projectNode.filter((node: any) => node?.nodeType === nodeType);
  };

  const calculateNodeProgress = (node: any): NodeProgressData => {
    const stats = node?.steelAssemblyStats || {};
    const pending = stats?.pending || 0;
    const inProgress = stats?.inProgress || 0;
    const completed = stats?.completed || 0;
    const total = pending + inProgress + completed;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      id: node?.id || "",
      name:
        node?.name ||
        node?.nodeName ||
        NODE_TYPE_LABELS[node?.nodeType as NodeType] ||
        "未命名",
      progress,
      completed,
      total,
    };
  };

  const getProgressDataForType = (nodeType: NodeType): NodeProgressData[] => {
    const nodes = getNodesOfType(nodeType);
    return nodes.map(calculateNodeProgress);
  };

  const getAverageProgressForType = (nodeType: NodeType): number => {
    const nodes = getNodesOfType(nodeType);
    if (nodes.length === 0) return 0;

    let totalCompleted = 0;
    let totalItems = 0;

    nodes.forEach((node: any) => {
      const stats = node?.steelAssemblyStats || {};
      const pending = stats?.pending || 0;
      const inProgress = stats?.inProgress || 0;
      const completed = stats?.completed || 0;
      totalCompleted += completed;
      totalItems += pending + inProgress + completed;
    });

    return totalItems > 0 ? Math.round((totalCompleted / totalItems) * 100) : 0;
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { date, remarks, partner, partnerAddress, name } = quotationData || {};
  const { city, district, postalCode, address } = partnerAddress || {};

  /** 專案已封存（後端 status = "close"），封存後不可再編輯 */
  const isArchived = (quotationData as any)?.status === "close";
  const canArchive =
    userRoles.includes("owner") ||
    userRoles.includes("admin") ||
    userRoles.includes("project");

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    if (typeof dateValue === "string") return dateValue;
    return dayjs(dateValue).format("YYYY-MM-DD");
  };

  const handleViewFile = (file) => {
    if (file) {
      window.open(file, "_blank");
    }
  };

  const isAnnotatableFile = (file: any) =>
    file?.folder === ANNOTATABLE_FOLDER && !!file?.url && !!docKindOf(file?.name);

  const handleViewOfficialFile = (file: any) => {
    if (isAnnotatableFile(file)) {
      window.open(
        documentAnnotateHref({ name: file.name, url: file.url }),
        "_blank",
      );
      return;
    }
    handleViewFile(file?.url || file);
  };

  const handleDownloadFile = async (fileObj) => {
    const url = fileObj?.url || fileObj;
    if (!url) return;
    const fileName = fileObj?.name || "download";
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      window.open(url, "_blank");
      api.error({
        message: t("quotation.downloadError"),
        description: t("quotation.downloadErrorDesc"),
        placement: "topLeft",
        duration: 3,
      });
    }
  };

  const openGoogleMaps = () => {
    const fullAddress = `${city || ""}${district || ""}${postalCode || ""}${address || ""}`;
    const encodedAddress = encodeURIComponent(fullAddress);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(googleMapsUrl, "_blank");
  };

  /** 已封存的專案不能再寫入 project（後端 project.update 會被擋），先在前端阻擋並提示 */
  const blockedByArchive = () => {
    if (!isArchived) return false;
    api.error({
      message: t("quotation.archived") || "已封存",
      description: t("quotation.archivedHint") || "此專案已封存，無法再編輯。",
      placement: "topLeft",
      duration: 3,
    });
    return true;
  };

  const handleFirstLayerUpload = (info: any) => {
    if (blockedByArchive()) return;
    const files = info.fileList
      .map((f: any) => f.originFileObj)
      .filter(Boolean);
    setPendingUploadFiles(files);
    setFolderSelectModalOpen(true);
  };

  const handleModalFolderSelectAndUpload = async (folderId: string) => {
    if (!pendingUploadFiles.length) return;
    if (blockedByArchive()) {
      setFolderSelectModalOpen(false);
      setPendingUploadFiles([]);
      return;
    }
    setFolderSelectModalOpen(false);
    setFileUploadState(true);
    const uploaded: any[] = [];
    for (const file of pendingUploadFiles) {
      try {
        const sasUrlData = await orpcClient.storage.createBlobSasUrl({
          fileName: file.name,
          contentType: file.type,
        });
        const uploadResponse = await fetch(sasUrlData.blobSasUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
          body: file,
        });
        if (uploadResponse.ok) {
          uploaded.push({
            id: Date.now() + Math.random(),
            name: file.name,
            url: sasUrlData.blobUrl,
            folder: folderId,
          });
        } else {
          api.error({
            message: t("quotation.uploadError") || "上傳失敗",
            description: `${file.name} 上傳失敗`,
            placement: "topLeft",
            duration: 1.5,
          });
        }
      } catch (error) {
        api.error({
          message: t("quotation.uploadError") || "上傳失敗",
          description: (error as any)?.message || `${file.name} 上傳失敗`,
          placement: "topLeft",
          duration: 3000,
        });
      }
    }
    if (uploaded.length) {
      // 先寫回 project 成功才更新畫面，否則會出現「說成功但重新整理就消失」
      const updated = [
        ...(Array.isArray(newFileUpload) ? (newFileUpload as any[]) : []),
        ...uploaded,
      ];
      try {
        await single(
          orpcClient.project.update([{ id: id!, attachment: updated }]),
        );
        setNewFileUpload(updated as any);
        api.success({
          message: t("quotation.uploadSuccess") || "上傳成功",
          description: `${uploaded.length} 個檔案已成功上傳`,
          placement: "topLeft",
          duration: 1.5,
        });
      } catch (error) {
        console.error("Error saving attachment:", error);
        api.error({
          message: t("quotation.uploadError") || "上傳失敗",
          description:
            (error as any)?.message ||
            t("quotation.fileUploadFailed") ||
            "公務檔案上傳失敗",
          placement: "topLeft",
          duration: 3,
        });
      }
    }
    setFileUploadState(false);
    setPendingUploadFiles([]);
  };

  const handleFolderUpload = async (files: File[]) => {
    if (!files?.length || !currentFolder) return;
    if (blockedByArchive()) return;
    setFileUploadState(true);
    const uploaded: any[] = [];
    for (const file of files) {
      try {
        const sasUrlData = await orpcClient.storage.createBlobSasUrl({
          fileName: file.name,
          contentType: file.type,
        });
        const uploadResponse = await fetch(sasUrlData.blobSasUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
          body: file,
        });
        if (uploadResponse.ok) {
          uploaded.push({
            id: Date.now() + Math.random(),
            name: file.name,
            url: sasUrlData.blobUrl,
            folder: currentFolder,
          });
        } else {
          api.error({
            message: t("quotation.uploadError") || "上傳失敗",
            description: `${file.name} ${t("quotation.fileUploadFailed") || "公務檔案上傳失敗"}`,
            placement: "topLeft",
            duration: 1.5,
          });
        }
      } catch (error) {
        api.error({
          message: t("quotation.uploadError") || "上傳失敗",
          description:
            (error as any)?.message ||
            t("quotation.fileUploadFailed") ||
            "公務檔案上傳失敗",
          placement: "topLeft",
          duration: 3000,
        });
      }
    }
    if (uploaded.length) {
      // 先寫回 project 成功才更新畫面，否則會出現「說成功但重新整理就消失」
      const updated = [
        ...(Array.isArray(newFileUpload) ? (newFileUpload as any[]) : []),
        ...uploaded,
      ];
      try {
        await single(
          orpcClient.project.update([{ id: id!, attachment: updated }]),
        );
        setNewFileUpload(updated as any);
        api.success({
          message: t("quotation.uploadSuccess") || "上傳成功",
          description:
            t("quotation.fileUploadedSuccessfully") || "檔案已成功上傳",
          placement: "topLeft",
          duration: 1.5,
        });
      } catch (error) {
        console.error("Error saving attachment:", error);
        api.error({
          message: t("quotation.uploadError") || "上傳失敗",
          description:
            (error as any)?.message ||
            t("quotation.fileUploadFailed") ||
            "公務檔案上傳失敗",
          placement: "topLeft",
          duration: 3,
        });
      }
    }
    setFileUploadState(false);
  };

  const handleChangeContractImg = async (info) => {
    if (blockedByArchive()) return;
    setContractUploadState(true);
    if (!contractUploadState) {
      const file = info.file.originFileObj;

      try {
        const sasUrlData = await orpcClient.storage.createBlobSasUrl({
          fileName: file.name,
          contentType: file.type,
        });

        const uploadResponse = await fetch(sasUrlData.blobSasUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
          body: file,
        });

        if (uploadResponse.ok) {
          const newContract = {
            id: Date.now(),
            name: file.name,
            url: sasUrlData.blobUrl,
          };
          await single(
            orpcClient.project.update([
              {
                id: id!,
                contractFile: newContract,
              },
            ])
          );
          setContractFileUpload([newContract] as any);
          api.success({
            message: t("quotation.uploadSuccess") || "上傳成功",
            description:
              t("quotation.contractUploadedSuccessfully") || "合約已成功上傳",
            placement: "topLeft",
            duration: 1.5,
          });
        } else {
          console.error("File upload failed:", uploadResponse.statusText);
          api.error({
            message: t("quotation.uploadError") || "上傳失敗",
            description:
              t("quotation.contractUploadFailed") || "財務檔案上傳失敗",
            placement: "topLeft",
            duration: 1.5,
          });
        }
      } catch (error) {
        console.error("Error uploading contract file:", error);
        api.error({
          message: t("quotation.uploadError") || "上傳失敗",
          description:
            error?.message ||
            t("quotation.contractUploadFailed") ||
            "財務檔案上傳失敗",
          placement: "topLeft",
          duration: 1.5,
        });
      }
      setContractUploadState(false);
    }
  };

  const handleDeleteFile = async (file) => {
    if (blockedByArchive()) return;
    try {
      const newFiles = newFileUpload.filter((f) => f.id !== file.id);
      await single(
        orpcClient.project.update([
          {
            id: id!,
            attachment: newFiles,
          },
        ])
      );
      setNewFileUpload(newFiles);
      api.success({
        message: t("quotation.deleteSuccess") || "刪除成功",
        description: t("quotation.fileDeletedSuccessfully") || "檔案已成功刪除",
        placement: "topLeft",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error deleting file:", error);
      api.error({
        message: t("quotation.deleteError") || "刪除失敗",
        description:
          error?.message || t("quotation.fileDeleteFailed") || "檔案刪除失敗",
        placement: "topLeft",
        duration: 3000,
      });
    }
  };

  const handleDeleteContractFile = async (file) => {
    if (blockedByArchive()) return;
    try {
      await single(
        orpcClient.project.update([
          {
            id: id!,
            contractFile: null,
          },
        ])
      );
      setContractFileUpload([]);
      api.success({
        message: t("quotation.deleteSuccess") || "刪除成功",
        description:
          t("quotation.contractDeletedSuccessfully") || "合約已成功刪除",
        placement: "topLeft",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error deleting contract file:", error);
      api.error({
        message: t("quotation.deleteError") || "刪除失敗",
        description:
          error?.message ||
          t("quotation.contractDeleteFailed") ||
          "合約刪除失敗",
        placement: "topLeft",
        duration: 3000,
      });
    }
  };

  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       let result = await apiUserGetPartners({
  //         companyId: localStorage.getItem("companyId"),
  //       });
  //       setPartnerData(result?.data);
  //     } catch (error) {
  //       console.error("Error fetching partner data:", error);
  //     }
  //   };

  //   fetchData();
  // }, []);

  useEffect(() => {
    setNewFileUpload([]);
    setContractFileUpload([]);
    const fetchData = async () => {
      try {
        const result = await single(
          orpcClient.project.read([{ id: id! }])
        );
        if (!result) return;
        setQuotationData(result as any);
        const attachment = result?.attachment;
        const normalizedAttachment = attachment
          ? Array.isArray(attachment)
            ? attachment.map((item, index) =>
                typeof item === "string"
                  ? {
                      id: `${Date.now()}-${index}`,
                      name: stripUuidPrefix(item.split("/").pop() || item),
                      url: item,
                    }
                  : { ...item, id: `${Date.now()}-${index}` },
              )
            : typeof attachment === "string"
              ? [
                  {
                    id: Date.now(),
                    name: stripUuidPrefix(
                      attachment.split("/").pop() || attachment,
                    ),
                    url: attachment,
                  },
                ]
              : [attachment]
          : [];
        setNewFileUpload(normalizedAttachment);
        setContractFileUpload(
          result?.contractFile ? [result?.contractFile] : [],
        );
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const [list, me] = await Promise.all([
          orpcClient.employee.listRole({}),
          apiUserGetMe(),
        ]);
        const currentMember = (Array.isArray(list) ? list : []).find(
          (u: any) => u.email === (me as { email: string })?.email,
        );
        if (currentMember) {
          const roles = [
            ...new Set<string>(
              (currentMember.role ?? "")
                .split(",")
                .map((r: string) => r.trim())
                .filter(Boolean),
            ),
          ];
          setUserRoles(roles);
        }
      } catch {}
    };
    fetchUserRoles();
  }, []);

  const matchedFiles = getMatchedFilesAcrossFolders();

  const handleArchive = () => {
    Modal.confirm({
      title: t("quotation.archiveConfirmTitle") || "確認封存此專案？",
      content:
        t("quotation.archiveConfirmContent") ||
        "封存後將無法再編輯此專案，也無法解除封存。確定要繼續嗎？",
      okText: t("quotation.archive") || "封存",
      okButtonProps: { danger: true },
      cancelText: t("quotation.cancel") || "取消",
      onOk: async () => {
        setArchiving(true);
        try {
          await single(orpcClient.project.close([{ id: id! }]));
          api.success({
            message: t("quotation.archiveSuccess") || "專案已封存",
          });
          navigate("/hrMainPage/projectQuotationArchived");
        } catch (error) {
          console.error("Error archiving project:", error);
          api.error({
            message: t("quotation.archiveFailed") || "封存失敗",
            description:
              error instanceof Error ? error.message : String(error),
          });
        } finally {
          setArchiving(false);
        }
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 m-5">
      {contextHolder}
      <div className="container mx-auto px-6 md:px-12 lg:px-16 py-8 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: "#6F6F6F" }}>
            {t("quotation.businessManagementSystem") || "業務管理系統"}
          </h1>
          <p className="text-gray-400 text-lg">
            {t("quotation.managePartnersAndOrders") || "管理合作夥伴與訂單資訊"}
          </p>
        </div>

        <Card className="mb-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between border-b pb-3 mb-3">
            {!basicInfoExpanded ? (
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <IconUser size={16} style={{ color: "#1890ff" }} />
                  <span className="font-semibold" style={{ color: "#6F6F6F" }}>
                    {name || t("quotation.noName") || "未命名"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <IconCalendar size={16} style={{ color: "#1890ff" }} />
                  <span style={{ color: "#6F6F6F" }}>{formatDate(date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <IconUser size={16} style={{ color: "#1890ff" }} />
                  <span className="text-gray-400">
                    {partner?.name
                      ? `${partner.name} (ID: ${partner.id})`
                      : t("quotation.noPartner") || "未設定合作方"}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                  <IconFileText size={16} style={{ color: "#1890ff" }} />
                </div>
                <h2
                  className="text-lg font-semibold"
                  style={{ color: "#6F6F6F" }}
                >
                  {t("quotation.basicInfo") || "基本資訊"}
                </h2>
              </div>
            )}
            <div className="flex items-center gap-2">
              {isArchived && (
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{
                    backgroundColor: "#F5F5F5",
                    color: "#8C8C8C",
                    border: "1px solid #D9D9D9",
                  }}
                >
                  {t("quotation.archived") || "已封存"}
                </span>
              )}
              {!basicInfoExpanded && (
                <Button
                  type="text"
                  shape="circle"
                  icon={<IconMap size={16} />}
                  onClick={openGoogleMaps}
                  className="hover:bg-blue-50"
                  title={
                    t("quotation.openGoogleMaps") || "在 Google 地圖中開啟"
                  }
                />
              )}
              {!isArchived && (
                <Button
                  type="text"
                  shape="circle"
                  icon={<IconPencil size={16} />}
                  onClick={() => navigate(`/hrMainPage/editQuotation/${id}`)}
                  className="hover:bg-blue-50"
                  title={t("quotation.edit") || "編輯"}
                />
              )}
              <Button
                type="text"
                shape="circle"
                icon={basicInfoExpanded ? <span>−</span> : <span>+</span>}
                onClick={() => setBasicInfoExpanded(!basicInfoExpanded)}
                className="hover:bg-blue-50"
                title={
                  basicInfoExpanded
                    ? t("quotation.collapse") || "收起"
                    : t("quotation.expand") || "展開"
                }
              />
            </div>
          </div>

          {basicInfoExpanded && (
            <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label
                    className="text-xs font-semibold flex items-center gap-1.5"
                    style={{ color: "#6F6F6F" }}
                  >
                    <IconUser size={14} style={{ color: "#1890ff" }} />
                    {t("quotation.name") || "名稱"}
                  </label>
                  <Input
                    disabled
                    value={name}
                    placeholder={t("quotation.namePlaceholder") || "請輸入名稱"}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    className="text-xs font-semibold flex items-center gap-1.5"
                    style={{ color: "#6F6F6F" }}
                  >
                    <IconCalendar size={14} style={{ color: "#1890ff" }} />
                    {t("quotation.date") || "日期"}
                  </label>
                  <Input
                    disabled
                    value={formatDate(date)}
                    placeholder={t("quotation.datePlaceholder") || "請選擇日期"}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    className="text-xs font-semibold flex items-center gap-1.5"
                    style={{ color: "#6F6F6F" }}
                  >
                    <IconUser size={14} style={{ color: "#1890ff" }} />
                    {t("quotation.partner") || "合作方"}
                  </label>
                  <Autocomplete
                    disabled
                    value={
                      partner?.name ? `${partner.name} (ID: ${partner.id})` : ""
                    }
                    placeholder={
                      t("quotation.partnerPlaceholder") || "請選擇合作方"
                    }
                    leftSection={<IconUser size={16} />}
                    data={partnerData?.map((p) => `${p?.name} (ID: ${p?.id})`)}
                    className="h-9"
                  />
                </div>
              </div>

              <div className="pt-3 border-t">
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <IconMapPin size={16} style={{ color: "#52c41a" }} />
                  {t("quotation.addressInfo") || "地址資訊"}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-semibold flex items-center gap-1.5"
                      style={{ color: "#6F6F6F" }}
                    >
                      <IconMapPin size={14} style={{ color: "#52c41a" }} />
                      {t("quotation.city") || "縣市"}
                    </label>
                    <Input
                      disabled
                      value={city}
                      placeholder={
                        t("quotation.cityPlaceholder") || "請輸入縣市"
                      }
                      className="h-9"
                      leftSection={<IconMapPin size={16} />}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-semibold flex items-center gap-1.5"
                      style={{ color: "#6F6F6F" }}
                    >
                      <IconMapPin size={14} style={{ color: "#52c41a" }} />
                      {t("quotation.district") || "行政區"}
                    </label>
                    <Input
                      disabled
                      value={district}
                      placeholder={
                        t("quotation.districtPlaceholder") || "請輸入行政區"
                      }
                      className="h-9"
                      leftSection={<IconMapPin size={16} />}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-semibold flex items-center gap-1.5"
                      style={{ color: "#6F6F6F" }}
                    >
                      <IconMapPin size={14} style={{ color: "#52c41a" }} />
                      {t("quotation.postalCode") || "郵遞區號"}
                    </label>
                    <Input
                      disabled
                      value={postalCode}
                      placeholder={
                        t("quotation.postalCodePlaceholder") || "請輸入郵遞區號"
                      }
                      className="h-9"
                      leftSection={<IconMapPin size={16} />}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-semibold flex items-center gap-1.5"
                      style={{ color: "#6F6F6F" }}
                    >
                      <IconMapPin size={14} style={{ color: "#52c41a" }} />
                      {t("quotation.fullAddress") || "完整地址"}
                    </label>
                    <Input
                      disabled
                      value={address}
                      placeholder={
                        t("quotation.fullAddressPlaceholder") ||
                        "請輸入完整地址"
                      }
                      className="h-9"
                      leftSection={<IconMapPin size={16} />}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t">
                <label
                  className="text-xs font-semibold flex items-center gap-1.5 mb-2"
                  style={{ color: "#6F6F6F" }}
                >
                  <IconFileText size={14} />
                  {t("quotation.remarks") || "備註"}
                </label>
                <AntdInput.TextArea
                  disabled
                  value={remarks}
                  placeholder={
                    t("quotation.remarksPlaceholder") || "請輸入備註資訊"
                  }
                  className="min-h-[100px] resize-none"
                />
              </div>
            </div>
          )}
        </Card>

        <Card className="mb-6 shadow-lg">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <IconBuilding size={20} style={{ color: "#1890ff" }} />
                <h2
                  className="text-xl font-semibold"
                  style={{ color: "#6F6F6F" }}
                >
                  {t("quotation.progressManagement") || "工程進度管理"}
                </h2>
              </div>
              <Button
                type="text"
                shape="circle"
                icon={<IconSettings size={20} />}
                onClick={() => setShowSettings(!showSettings)}
              />
            </div>

            {showSettings && (
              <Card className="mb-6" style={{ background: "#fafafa" }}>
                <h3
                  className="text-base font-semibold mb-4"
                  style={{ color: "#6F6F6F" }}
                >
                  {t("quotation.thresholdSettings") || "進度閾值設置"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <span
                      className="text-sm font-medium"
                      style={{ color: "#6F6F6F" }}
                    >
                      {t("quotation.lowThreshold") || "低閾值（紅色）"}
                    </span>
                    <InputNumber
                      min={0}
                      max={highThreshold ?? 100}
                      value={lowThreshold}
                      onChange={(val) => setLowThreshold(val)}
                      style={{ width: 80 }}
                    />
                    <span className="text-sm text-gray-400">
                      % 以下顯示紅色
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className="text-sm font-medium"
                      style={{ color: "#6F6F6F" }}
                    >
                      {t("quotation.highThreshold") || "高閾值（綠色）"}
                    </span>
                    <InputNumber
                      min={lowThreshold ?? 0}
                      max={100}
                      value={highThreshold}
                      onChange={(val) => setHighThreshold(val)}
                      style={{ width: 80 }}
                    />
                    <span className="text-sm text-gray-400">
                      % 以上顯示綠色
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-400 mt-3">
                  {lowThreshold ?? 30}% 至 {highThreshold ?? 70}% 之間顯示藍色
                </p>
              </Card>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <CircularProgress
                value={getAverageProgressForType("building")}
                label={NODE_TYPE_LABELS.building}
                isActive={activeProgressSection === "building"}
                onClick={() => setActiveProgressSection("building")}
                lowThreshold={lowThreshold ?? 30}
                highThreshold={highThreshold ?? 70}
              />
              <CircularProgress
                value={getAverageProgressForType("zone")}
                label={NODE_TYPE_LABELS.zone}
                isActive={activeProgressSection === "zone"}
                onClick={() => setActiveProgressSection("zone")}
                lowThreshold={lowThreshold ?? 30}
                highThreshold={highThreshold ?? 70}
              />
              <CircularProgress
                value={getAverageProgressForType("batch")}
                label={NODE_TYPE_LABELS.batch}
                isActive={activeProgressSection === "batch"}
                onClick={() => setActiveProgressSection("batch")}
                lowThreshold={lowThreshold ?? 30}
                highThreshold={highThreshold ?? 70}
              />
              <CircularProgress
                value={getAverageProgressForType("package")}
                label={NODE_TYPE_LABELS.package}
                isActive={activeProgressSection === "package"}
                onClick={() => setActiveProgressSection("package")}
                lowThreshold={lowThreshold ?? 30}
                highThreshold={highThreshold ?? 70}
              />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-lg font-semibold"
                  style={{ color: "#6F6F6F" }}
                >
                  {NODE_TYPE_LABELS[activeProgressSection]}
                </h3>
                <span className="text-sm text-gray-400">
                  {t("quotation.processedComponents") || "已加工構件"}
                </span>
              </div>

              {getProgressDataForType(activeProgressSection).length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  {t("quotation.noProgressData") || "暫無進度資料"}
                </div>
              ) : (
                <div>
                  {getProgressDataForType(activeProgressSection).map((item) => (
                    <WorkItemProgress
                      key={item.id}
                      name={item.name}
                      progress={item.progress}
                      completed={item.completed}
                      total={item.total}
                      lowThreshold={lowThreshold ?? 30}
                      highThreshold={highThreshold ?? 70}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card className="mb-6 shadow-lg">
          <div className="space-y-6 p-6">
            <div className="flex items-center gap-3 mb-4">
              <IconPackage size={20} style={{ color: "#1890ff" }} />
              <h2
                className="text-xl font-semibold"
                style={{ color: "#6F6F6F" }}
              >
                {t("productionPlan.order") || "訂單"}
              </h2>
            </div>

            <div>
              {/* <h3
                className="text-xl font-semibold mb-4 flex items-center gap-2"
                style={{ color: "#6F6F6F" }}
              >
                <IconPackage size={20} />
                {t("productionPlan.bomTable") || "BOM表"}
              </h3> */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button
                  size="large"
                  className="h-14 flex items-center justify-center gap-2 text-base font-medium"
                  onClick={() => navigate(`/hrMainPage/steelAssemblyBOM/${id}`)}
                >
                  {t("hrMain.materialBOM") || "材料BOM"}
                  <IconArrowRight size={16} />
                </Button>
                <Button
                  size="large"
                  className="h-14 flex items-center justify-center gap-2 text-base font-medium"
                  onClick={() => navigate(`/hrMainPage/steelBoltBOM/${id}`)}
                >
                  {t("hrMain.boltBOM") || "螺絲BOM"}
                  <IconArrowRight size={16} />
                </Button>
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="flex items-center gap-3 mb-4">
                <IconFileText size={20} style={{ color: "#1890ff" }} />
                <h2
                  className="text-xl font-semibold"
                  style={{ color: "#6F6F6F" }}
                >
                  {t("productionPlan.production") || "生產"}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button
                  size="large"
                  className="h-14 flex items-center justify-center gap-2 text-base font-medium"
                  onClick={() =>
                    navigate(
                      `/hrMainPage/designBOM/${quotationData?.projectNode?.find((node: any) => node.nodeType === "building")?.id}?quotationId=${id}`,
                    )
                  }
                >
                  {t("productionPlan.designBOM") || "設計BOM"}
                  <IconArrowRight size={16} />
                </Button>
                <Button
                  size="large"
                  className="h-14 flex items-center justify-center gap-2 text-base font-medium"
                  onClick={() => window.open(`/productionPlan/${id}`, "_blank")}
                >
                  {t("productionPlan.productionPlanTable") || "生產計畫表"}
                  <IconArrowRight size={16} />
                </Button>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3
                className="text-xl font-semibold mb-4 flex items-center gap-2"
                style={{ color: "#6F6F6F" }}
              >
                <IconUpload size={20} />
                {t("quotation.fileUpload") || "公務檔案"}
              </h3>

              <div
                className="flex flex-wrap items-center gap-3 px-4 py-3 mb-4"
                style={{ backgroundColor: "#F5F5F5", borderRadius: "8px" }}
              >
                <AntdInput
                  allowClear
                  prefix={<IconSearch size={16} style={{ color: "#9CA3AF" }} />}
                  placeholder={t("quotation.searchFile") || "搜尋檔案名稱"}
                  value={fileSearch}
                  onChange={(e) => setFileSearch(e.target.value)}
                  style={{ width: 220 }}
                />
                <Select
                  value={fileFolderFilter}
                  onChange={(value) => setFileFolderFilter(value)}
                  style={{ width: 200 }}
                  options={[
                    {
                      value: "all",
                      label: t("quotation.allFolders") || "全部資料夾",
                    },
                    ...FOLDERS.map((f) => ({ value: f.id, label: f.name })),
                  ]}
                />
                {isFileFilterActive && (
                  <Button
                    onClick={() => {
                      setFileSearch("");
                      setFileFolderFilter("all");
                    }}
                  >
                    {t("quotation.clear") || "清除"}
                  </Button>
                )}
              </div>

              {currentFolder === null && isFileFilterActive ? (
                <div>
                  <div className="mb-3 text-sm" style={{ color: "#6F6F6F" }}>
                    {t("quotation.searchResults") || "搜尋結果"}：
                    {matchedFiles.length} {t("quotation.files") || "個檔案"}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {matchedFiles.length === 0 && (
                      <div className="col-span-full text-center text-gray-400 py-8">
                        {t("quotation.noMatchedFiles") || "沒有符合條件的檔案"}
                      </div>
                    )}
                    {matchedFiles.map((file: any) => {
                      const folderLabel =
                        FOLDERS.find((f) => f.id === file?.folder)?.name ||
                        file?.folder;
                      return (
                        <div
                          key={file?.id}
                          className="border-2 rounded-xl p-8 hover:border-blue-400 transition-all min-h-[200px]"
                        >
                          <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                              <IconFileText
                                size={32}
                                style={{ color: "#1890ff" }}
                              />
                            </div>
                            <div>
                              <p
                                className="font-semibold truncate max-w-[200px]"
                                style={{ color: "#6F6F6F" }}
                                title={file?.name}
                              >
                                {file?.name}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {folderLabel}
                              </p>
                            </div>
                            <div className="flex gap-2 mt-2">
                              <Button
                                type="link"
                                icon={<IconEye size={16} />}
                                onClick={() => handleViewOfficialFile(file)}
                                title={
                                  isAnnotatableFile(file)
                                    ? t("docAnnotate.viewDocument") || "檢視文件"
                                    : t("quotation.viewFile") || "查看檔案"
                                }
                              />
                              <Button
                                type="link"
                                icon={<IconDownload size={16} />}
                                onClick={() => handleDownloadFile(file)}
                                title={
                                  t("quotation.downloadFile") || "下載檔案"
                                }
                              />
                              {!isArchived && (
                                <Button
                                  type="link"
                                  danger
                                  onClick={() => handleDeleteFile(file)}
                                  title={t("quotation.deleteFile") || "刪除"}
                                >
                                  {t("quotation.delete") || "刪除"}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : currentFolder === null ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Upload
                    name="avatar"
                    showUploadList={false}
                    multiple
                    disabled={isArchived}
                    onChange={handleFirstLayerUpload}
                    className="w-full h-full"
                  >
                    <Spin spinning={fileUploadState} className="h-full">
                      <div
                        className={`border-2 border-dashed rounded-xl p-8 transition-all h-full ${
                          isArchived
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:border-blue-400 cursor-pointer"
                        }`}
                      >
                        <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                            <IconUpload
                              size={32}
                              style={{ color: "#1890ff" }}
                            />
                          </div>
                          <div>
                            <p
                              className="font-semibold"
                              style={{ color: "#6F6F6F" }}
                            >
                              {t("quotation.clickToUpload") || "點擊上傳檔案"}
                            </p>
                            <p className="text-sm text-gray-400">
                              {t("quotation.supportedFormats") ||
                                "支援各種文件格式"}
                            </p>
                          </div>
                          <Button className="mt-2">
                            {t("quotation.addFile") || "新增檔案"}
                          </Button>
                        </div>
                      </div>
                    </Spin>
                  </Upload>
                  {FOLDERS.map((folder) => {
                    const fileCount = getFilesInFolder(folder.id).length;
                    return (
                      <div
                        key={folder.id}
                        onClick={() => setCurrentFolder(folder.id)}
                        className="border-2 border-solid rounded-xl p-8 hover:border-blue-400 transition-all cursor-pointer bg-white"
                      >
                        <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                            <IconFolder
                              size={32}
                              style={{ color: "#1890ff" }}
                            />
                          </div>
                          <p
                            className="font-semibold text-lg"
                            style={{ color: "#6F6F6F" }}
                          >
                            {folder.name}
                          </p>
                          <p className="text-sm text-gray-400">
                            {fileCount} {t("quotation.files") || "個檔案"}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Button
                      icon={<IconArrowLeft size={16} />}
                      onClick={() => setCurrentFolder(null)}
                    >
                      {t("quotation.backToFolders") || "返回資料夾"}
                    </Button>
                    <span
                      className="text-lg font-semibold"
                      style={{ color: "#6F6F6F" }}
                    >
                      {FOLDERS.find((f) => f.id === currentFolder)?.name ||
                        currentFolder}
                    </span>
                    <Upload
                      name="file"
                      showUploadList={false}
                      multiple
                      disabled={isArchived}
                      beforeUpload={(file, fileList) => {
                        if (file === fileList[0]) {
                          handleFolderUpload(fileList);
                        }
                        return false;
                      }}
                    >
                      <Spin spinning={fileUploadState}>
                        <Button icon={<IconUpload size={16} />}>
                          {t("quotation.addFile") || "新增檔案"}
                        </Button>
                      </Spin>
                    </Upload>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {getFilesInFolder(currentFolder).length === 0 && (
                      <div className="col-span-full text-center text-gray-400 py-8">
                        {t("quotation.noFiles") || "此資料夾沒有檔案"}
                      </div>
                    )}
                    {getFilesInFolder(currentFolder).map((file: any) => (
                      <div
                        key={file?.id}
                        className="border-2 rounded-xl p-8 hover:border-blue-400 transition-all min-h-[200px]"
                      >
                        <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                            <IconFileText
                              size={32}
                              style={{ color: "#1890ff" }}
                            />
                          </div>
                          <div>
                            <p
                              className="font-semibold truncate max-w-[200px]"
                              style={{ color: "#6F6F6F" }}
                              title={file?.name}
                            >
                              {file?.name}
                            </p>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button
                              type="link"
                              icon={<IconEye size={16} />}
                              onClick={() => handleViewOfficialFile(file)}
                              title={
                                isAnnotatableFile(file)
                                  ? t("docAnnotate.viewDocument") || "檢視文件"
                                  : t("quotation.viewFile") || "查看檔案"
                              }
                            />
                            <Button
                              type="link"
                              icon={<IconDownload size={16} />}
                              onClick={() => handleDownloadFile(file)}
                              title={t("quotation.downloadFile") || "下載檔案"}
                            />
                            {!isArchived && (
                              <Button
                                type="link"
                                danger
                                onClick={() => handleDeleteFile(file)}
                                title={t("quotation.deleteFile") || "刪除"}
                              >
                                {t("quotation.delete") || "刪除"}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {(userRoles.includes("owner") ||
              userRoles.includes("admin") ||
              userRoles.includes("finance")) && (
              <div className="border-t pt-6">
                <h3
                  className="text-xl font-semibold mb-4 flex items-center gap-2"
                  style={{ color: "#6F6F6F" }}
                >
                  <IconFileText size={20} />
                  {t("quotation.contractUpload") || "財務檔案"}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Upload
                    name="contract"
                    showUploadList={false}
                    disabled={isArchived}
                    onChange={async (info) => {
                      await handleChangeContractImg(info);
                    }}
                    className="w-full"
                  >
                    <Spin spinning={contractUploadState}>
                      <div
                        className={`border-2 border-dashed rounded-xl p-8 transition-all min-h-[200px] ${
                          isArchived
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:border-green-400 cursor-pointer"
                        }`}
                      >
                        <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                            <IconFileText
                              size={32}
                              style={{ color: "#52c41a" }}
                            />
                          </div>
                          <div>
                            <p
                              className="font-semibold"
                              style={{ color: "#6F6F6F" }}
                            >
                              {t("quotation.clickToUploadContract") ||
                                "上傳合約文件"}
                            </p>
                            <p className="text-sm text-gray-400">
                              {t("quotation.supportedContractFormats") ||
                                "支援 PDF、Word 等格式"}
                            </p>
                          </div>
                          <Button className="mt-2">
                            {t("quotation.addFile") || "新增檔案"}
                          </Button>
                        </div>
                      </div>
                    </Spin>
                  </Upload>
                  {Array.isArray(contractFileUpload) &&
                    contractFileUpload.map((file) => (
                      <div
                        key={file?.id}
                        className="border-2 border-solid rounded-xl p-8 hover:border-blue-400 transition-all min-h-[200px] bg-white"
                      >
                        <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                            <IconFileText
                              size={32}
                              style={{ color: "#1890ff" }}
                            />
                          </div>
                          <div>
                            <p
                              className="font-semibold truncate max-w-[200px]"
                              style={{ color: "#6F6F6F" }}
                              title={file?.name}
                            >
                              {file?.name}
                            </p>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button
                              type="link"
                              icon={<IconEye size={16} />}
                              onClick={() => handleViewFile(file?.url || file)}
                              title={t("quotation.viewFile") || "查看檔案"}
                            />
                            <Button
                              type="link"
                              icon={<IconDownload size={16} />}
                              onClick={() => handleDownloadFile(file)}
                              title={t("quotation.downloadFile") || "下載檔案"}
                            />
                            {!isArchived && (
                              <Button
                                type="link"
                                danger
                                onClick={() => handleDeleteContractFile(file)}
                                title={t("quotation.deleteFile") || "刪除"}
                              >
                                {t("quotation.delete") || "刪除"}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {canArchive && (
              <div className="border-t pt-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3
                      className="text-xl font-semibold mb-1 flex items-center gap-2"
                      style={{ color: "#6F6F6F" }}
                    >
                      <IconArchive size={20} />
                      {t("quotation.archiveProject") || "封存專案"}
                    </h3>
                    <p className="text-sm text-gray-400 max-w-2xl">
                      {isArchived
                        ? t("quotation.archivedHint") ||
                          "此專案已封存，無法再編輯。"
                        : t("quotation.archiveDescription") ||
                          "封存後專案會移到「已封存」清單，並且無法再編輯專案資料（含生產計劃儲存）。此操作無法復原。"}
                    </p>
                  </div>
                  <Button
                    danger
                    icon={<IconArchive size={16} />}
                    disabled={isArchived}
                    loading={archiving}
                    onClick={handleArchive}
                  >
                    {isArchived
                      ? t("quotation.archived") || "已封存"
                      : t("quotation.archive") || "封存"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      <Modal
        title={t("quotation.selectFolder") || "選擇資料夾"}
        open={folderSelectModalOpen}
        onCancel={() => {
          setFolderSelectModalOpen(false);
          setPendingUploadFiles([]);
        }}
        footer={null}
        width={600}
      >
        <p className="mb-4 text-gray-500">
          {t("quotation.selectFolderDescription") || "請選擇要上傳到哪個資料夾"}
        </p>
        <div className="grid grid-cols-2 gap-3">
          {FOLDERS.map((folder) => (
            <div
              key={folder.id}
              onClick={() => handleModalFolderSelectAndUpload(folder.id)}
              className="border-2 border-solid rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <IconFolder size={20} style={{ color: "#1890ff" }} />
                </div>
                <p className="font-semibold" style={{ color: "#6F6F6F" }}>
                  {folder.name}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}

export default QuotationPage;
